import { formatDecimal } from "@aclymatepackages/formatters";

import { SHARE_ACTION_TYPES } from "./cards/actionHandlers";

export const STALE_THRESHOLD_MS = 180 * 24 * 60 * 60 * 1000;

const isSurveySourcedTransactionForSubcategory = (targetSubcategory) => ({
  source,
  subcategory
}) => source === "vendor-survey" && subcategory === targetSubcategory;

const hasSurveyVehicleFuelTransaction = (rawTransactions) =>
  (rawTransactions || []).some(
    isSurveySourcedTransactionForSubcategory("fuel")
  );

const hasSurveySpendBasedTransaction = (rawTransactions) =>
  (rawTransactions || []).some(
    isSurveySourcedTransactionForSubcategory("spend-based")
  );

const toEmissionYear = (date) => {
  if (!date) {
    return null;
  }
  const dateObj = typeof date.toDate === "function" ? date.toDate() : date;
  const parsed = new Date(dateObj);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getFullYear();
};

const hasSurveyScopeTonsEntry = (dbEmissions, year, scope) =>
  (dbEmissions || []).some(
    (emission) =>
      emission?.source === "vendor-survey" &&
      Number(emission?.scope) === scope &&
      Number(emission?.volume) > 0 &&
      (!year || toEmissionYear(emission?.date) === Number(year))
  );

const SURVEY_SECTIONS = [
  {
    name: "Company basics",
    description: "Industry, revenue, employees, address",
    fieldIds: [
      "vendor:industry",
      "vendor:revenueYears",
      "vendor:employeeCount",
      "vendor:isRemote",
      "vendor:address"
    ],
    isComplete: ({ accountData }) =>
      Boolean(
        accountData?.industry &&
        (accountData?.revenueYears || []).length > 0 &&
        accountData?.employeeCount &&
        accountData?.address
      )
  },
  {
    name: "Facilities",
    description: "Offices, electricity, and gas use",
    fieldIds: ["facility:0"],
    isComplete: ({
      accountData,
      hasOffices,
      hasScope1SurveyTons,
      hasScope2SurveyTons
    }) =>
      Boolean(accountData?.isRemote) ||
      hasOffices ||
      (hasScope1SurveyTons && hasScope2SurveyTons)
  },
  {
    name: "Vehicles",
    description: "Company-owned fleet fuel",
    fieldIds: ["vehicle-fuel:all", "vendor:hasNoCompanyVehicles"],
    isComplete: ({
      accountData,
      hasSurveyVehicleFuel,
      hasScope1SurveyTons
    }) =>
      Boolean(accountData?.hasNoCompanyVehicles) ||
      hasSurveyVehicleFuel ||
      hasScope1SurveyTons
  },
  {
    name: "Spend-based emissions",
    description: "Major spending categories — supplies, travel, services",
    fieldIds: [],
    fieldIdMatch: (fieldId) => fieldId?.startsWith("transactions:"),
    isComplete: ({ hasSurveySpendBased, hasScope3SurveyTons }) =>
      hasSurveySpendBased || hasScope3SurveyTons
  }
];

const matchesSection = (section, fieldId) => {
  if (section.fieldIds.includes(fieldId)) {
    return true;
  }
  if (typeof section.fieldIdMatch === "function") {
    return section.fieldIdMatch(fieldId);
  }
  return false;
};

const deriveSectionStatus = (section, ctx) => {
  const skipped = ctx.skippedFields || [];
  const unknown = ctx.unknownFields || [];
  const sectionSkipped = skipped.some((id) => matchesSection(section, id));
  const sectionUnknown = unknown.some((id) => matchesSection(section, id));
  if (section.isComplete(ctx)) {
    return "complete";
  }
  if (sectionSkipped) {
    return "skipped";
  }
  if (sectionUnknown) {
    return "unknown";
  }
  return "pending";
};

export const computeSurveyProgress = ({
  accountData,
  vendorSurveysEntry,
  hasOffices,
  rawTransactions,
  dbEmissions,
  year
}) => {
  const ctx = {
    accountData,
    hasOffices,
    hasSurveyVehicleFuel: hasSurveyVehicleFuelTransaction(rawTransactions),
    hasSurveySpendBased: hasSurveySpendBasedTransaction(rawTransactions),
    hasScope1SurveyTons: hasSurveyScopeTonsEntry(dbEmissions, year, 1),
    hasScope2SurveyTons: hasSurveyScopeTonsEntry(dbEmissions, year, 2),
    hasScope3SurveyTons: hasSurveyScopeTonsEntry(dbEmissions, year, 3),
    skippedFields: vendorSurveysEntry?.skippedFields || [],
    unknownFields: vendorSurveysEntry?.unknownFields || []
  };
  const sections = SURVEY_SECTIONS.map((section) => ({
    name: section.name,
    description: section.description,
    status: deriveSectionStatus(section, ctx)
  }));
  const filterByStatus = (status) =>
    sections.filter((s) => s.status === status).map((s) => s.name);
  return {
    sections,
    completedSections: filterByStatus("complete"),
    skippedSections: filterByStatus("skipped"),
    unknownSections: filterByStatus("unknown"),
    pendingSections: filterByStatus("pending")
  };
};

const STATE_RULES = [
  { state: "empty-fallback", match: (ctx) => !ctx },
  {
    state: "buyer-invited",
    match: (ctx) =>
      Boolean(ctx.arrivedFromBuyerInviteId) && ctx.surveyStatus !== "complete"
  },
  {
    state: "survey-in-progress",
    match: (ctx) =>
      ctx.surveyStatus === "partial" ||
      ctx.surveyStatus === "initial-estimate-ready"
  },
  {
    state: "post-onboarding-no-survey",
    match: (ctx) =>
      ctx.onboardingStatus === "complete" && ctx.surveyStatus === "none"
  },
  {
    state: "survey-complete-pending-requests",
    match: (ctx) =>
      ctx.surveyStatus === "complete" &&
      (ctx.pendingRequestsSummary?.surveyCount > 0 ||
        ctx.pendingRequestsSummary?.productCount > 0)
  },
  {
    state: "survey-stale",
    match: (ctx) =>
      ctx.surveyStatus === "complete" && ctx.surveyAge > STALE_THRESHOLD_MS
  },
  {
    state: "survey-complete-no-emissions-data",
    match: (ctx) =>
      ctx.surveyStatus === "complete" && (Number(ctx.totalTons) || 0) === 0
  },
  {
    state: "survey-complete-with-emissions",
    match: (ctx) =>
      ctx.surveyStatus === "complete" && (Number(ctx.totalTons) || 0) > 0
  }
];

export const resolveSurfaceState = (ctx) =>
  (
    STATE_RULES.find(({ match }) => match(ctx || {})) || {
      state: "empty-fallback"
    }
  ).state;

const DEFAULT_TAB_RULES = [
  {
    tab: "products",
    match: (ctx) => (ctx.pendingRequestsSummary?.productCount || 0) > 0
  },
  {
    tab: "customers",
    match: (ctx) => (ctx.pendingRequestsSummary?.surveyCount || 0) > 0
  },
  {
    tab: "suppliers",
    match: (ctx) => (ctx.pendingSupplierShares?.length || 0) > 0
  },
  { tab: "dashboard", match: () => true }
];

export const resolveDefaultTab = (ctx) =>
  (
    DEFAULT_TAB_RULES.find(({ match }) => match(ctx || {})) || {
      tab: "dashboard"
    }
  ).tab;

const FALLBACK_OPENER = {
  greeting: "Hi, I'm Scout. How can I help?",
  suggestedChips: [
    {
      label: "Show me my footprint",
      prompt: "What's my carbon footprint estimate?"
    },
    {
      label: "Talk to a human",
      clientAction: { type: "talk_to_a_human" }
    }
  ],
  cards: []
};

const takeSurveyCard = {
  type: "action_card",
  props: {
    label: "Take the emissions survey",
    actionType: "take_emissions_survey",
    description: "About 15 minutes, walked through by Scout."
  }
};

const resumeSurveyCard = {
  type: "action_card",
  props: {
    label: "Resume the emissions survey",
    actionType: "resume_emissions_survey",
    description: "Pick up where you left off."
  }
};

const updateRevenueCard = {
  type: "action_card",
  props: {
    label: "Update my revenue",
    actionType: "resume_emissions_survey",
    description: "Jump back into the survey to fill in revenue for this year."
  }
};

const buildBuyerInvitedGreeting = (buyerName) =>
  `${buyerName || "A customer"} asked you to share your footprint. The fastest way is to take a short emissions survey — about 15 minutes and I'll walk you through it.`;

const pluralizeRequests = (count) =>
  count === 1
    ? "1 customer is waiting on data from you"
    : `${count} customers are waiting on data from you`;

const buildPendingRequestsGreeting = (ctx) => {
  const totalPending =
    (ctx.pendingRequestsSummary?.surveyCount || 0) +
    (ctx.pendingRequestsSummary?.productCount || 0);
  return `Welcome back. ${pluralizeRequests(totalPending)} — want to handle those first?`;
};

const monthsFromMs = (ms) =>
  Math.max(1, Math.round(ms / (30 * 24 * 60 * 60 * 1000)));

const buildSurveyStaleGreeting = (ctx) => {
  const months = Number.isFinite(ctx.surveyAge)
    ? monthsFromMs(ctx.surveyAge)
    : null;
  const refreshTarget = mostRecentRecommendableYear();
  if (!months) {
    return `Your last survey is getting old. Want to refresh it for ${refreshTarget}?`;
  }
  return `Your last survey was ${months} months ago. Want to refresh it for ${refreshTarget}?`;
};

const findScopeShare = (scopePieSeries, scopeLabel) => {
  const total = (scopePieSeries || []).reduce(
    (sum, { value }) => sum + (Number(value) || 0),
    0
  );
  const match = (scopePieSeries || []).find(
    ({ label }) => label === scopeLabel
  );
  if (!match || total <= 0) {
    return 0;
  }
  return Math.round(((Number(match.value) || 0) / total) * 100);
};

const buildYearChips = (ctx) =>
  (ctx.availableYears || [])
    .filter((year) => Number(year) !== Number(ctx.year))
    .map((year) => ({
      label: `Show me ${year}`,
      prompt: `Show me my footprint for ${year}.`,
      clientAction: { type: "switchYear", payload: { year: Number(year) } }
    }));

const Q1_END_MONTH_INDEX = 3;

// Kept as its own constant rather than importing helpers/components/vendors's
// EARLIEST_BUYER_SURVEY_YEAR: that module chains into helpers/firebase.js ->
// firebase/performance, an ESM-only package Jest can't parse, which breaks
// this file's whole scoutChipAudit suite. scoutOpeners.js has no Firebase
// imports today — keep it that way. Update both constants together if the
// floor year ever changes.
const EARLIEST_BUYER_SURVEY_YEAR = 2021;

const mostRecentRecommendableYear = (now = new Date()) => {
  const currentYear = now.getFullYear();
  const isPastQ1 = now.getMonth() >= Q1_END_MONTH_INDEX;
  return isPastQ1 ? currentYear - 1 : currentYear - 2;
};

// Offers exactly one prior year at a time: the most recent year in
// [EARLIEST_BUYER_SURVEY_YEAR, mostRecentRecommendableYear()] that isn't
// already surveyed. Walks the actual range rather than assuming completed
// years are contiguous — a gap (e.g. 2025 and 2022 done but not 2024/2023,
// from a manual survey submission or historic import) must still surface
// the true next year (2024), not skip straight past it.
const nextTakeableYear = (surveyedYears) => {
  const surveyed = new Set(surveyedYears);
  const mostRecent = mostRecentRecommendableYear();
  const candidateYears = Array.from(
    { length: Math.max(mostRecent - EARLIEST_BUYER_SURVEY_YEAR + 1, 0) },
    (_, i) => mostRecent - i
  );
  return candidateYears.find((year) => !surveyed.has(year));
};

const buildTakeOtherYearChips = (ctx) => {
  const surveyedYears = (ctx.availableYears || []).map((year) => Number(year));
  const nextYear = nextTakeableYear(surveyedYears);
  return nextYear >= EARLIEST_BUYER_SURVEY_YEAR
    ? [
        {
          label: `Take the ${nextYear} survey`,
          clientAction: {
            type: "take_emissions_survey",
            payload: { year: nextYear }
          }
        }
      ]
    : [];
};

const buildHeadlineMetricCard = (ctx) => ({
  type: "metric_card",
  props: {
    label: `Total estimated emissions, ${ctx.year}`,
    value: formatDecimal(Number(ctx.totalTons) || 0),
    unit: "tCO2e",
    confidence: "Low",
    helpText:
      "Connect real business data in Navigator to raise confidence above Low."
  }
});

const buildWithEmissionsGreeting = (ctx) => {
  const scope3Share = findScopeShare(ctx.scopePieSeries, "Scope 3");
  const tonsLabel = formatDecimal(Number(ctx.totalTons) || 0);
  if (scope3Share > 0) {
    return `For ${ctx.year}, your estimated footprint is ${tonsLabel} tCO2e. Scope 3 is ${scope3Share}% of the picture — let me walk you through it.`;
  }
  return `For ${ctx.year}, your estimated footprint is ${tonsLabel} tCO2e. Let me walk you through what's behind it.`;
};

const buildEmptyTabOpener = (label) => () => ({
  greeting: `Welcome to ${label}. I'm Scout — ask me anything about this view.`,
  suggestedChips: [
    {
      label: "Show me my footprint",
      prompt: "What's my carbon footprint estimate?"
    }
  ],
  cards: []
});

const FOOTPRINT_OPENERS = {
  "post-onboarding-no-survey": () => ({
    greeting:
      "Welcome! Onboarding's done — the emissions survey is next. About 15 minutes, and I'll walk you through it.",
    suggestedChips: [
      {
        label: "Start the survey",
        clientAction: { type: "take_emissions_survey" }
      },
      {
        label: "What does the survey ask?",
        prompt: "What kind of questions does the emissions survey ask?"
      },
      {
        label: "How long does it take?",
        prompt: "How long does the emissions survey take to complete?"
      },
      {
        label: "Talk to a human",
        clientAction: { type: "talk_to_a_human" }
      }
    ],
    cards: [takeSurveyCard]
  }),
  "survey-in-progress": () => ({
    greeting:
      "Welcome back. Looks like you have a survey in progress — want to pick up where you left off?",
    suggestedChips: [
      {
        label: "Resume the survey",
        clientAction: { type: "resume_emissions_survey" }
      },
      {
        label: "What's left?",
        prompt: "Which parts of the survey do I still need to complete?"
      },
      {
        label: "Why does Scout ask these?",
        prompt: "Why does Scout ask about these specific things in the survey?"
      }
    ],
    cards: [resumeSurveyCard]
  }),
  "buyer-invited": (ctx) => ({
    greeting: buildBuyerInvitedGreeting(ctx.buyerName),
    suggestedChips: [
      {
        label: "Start the survey",
        clientAction: { type: "take_emissions_survey" }
      },
      {
        label: "Why is this customer asking?",
        prompt: "Why is my customer asking me to share my footprint?"
      },
      {
        label: "What will I be sharing?",
        prompt:
          "What data will be shared with my customer when I complete the survey?"
      },
      {
        label: "Talk to a human",
        clientAction: { type: "talk_to_a_human" }
      }
    ],
    cards: [takeSurveyCard]
  }),
  "survey-complete-pending-requests": (ctx) => ({
    greeting: buildPendingRequestsGreeting(ctx),
    suggestedChips: [
      {
        label: "Show me what's pending",
        prompt: "Show me my pending customer requests."
      },
      {
        label: "Show me my biggest emissions",
        prompt: "Show me my biggest emission sources."
      }
    ],
    cards: []
  }),
  "survey-stale": (ctx) => {
    const targetYear = mostRecentRecommendableYear();
    return {
      greeting: buildSurveyStaleGreeting(ctx),
      suggestedChips: [
        {
          label: `Refresh the ${targetYear} survey`,
          clientAction: {
            type: "take_emissions_survey",
            payload: { year: targetYear }
          }
        },
        {
          label: "What's changed?",
          prompt: "What's changed in my footprint since my last survey?"
        },
        {
          label: "Why refresh it?",
          prompt: "Why do I need to refresh my survey?"
        }
      ],
      cards: [takeSurveyCard]
    };
  },
  "survey-complete-no-emissions-data": (ctx) => ({
    greeting: `Your survey is in but I'm missing revenue for ${ctx.year} so I can't show your full footprint yet. Want to update it?`,
    suggestedChips: [
      {
        label: "Update my revenue",
        clientAction: { type: "resume_emissions_survey" }
      },
      {
        label: "Why does revenue matter?",
        prompt: "Why does revenue affect my footprint calculation?"
      }
    ],
    cards: [updateRevenueCard]
  }),
  "survey-complete-with-emissions": (ctx) => ({
    greeting: buildWithEmissionsGreeting(ctx),
    suggestedChips: [
      ...buildYearChips(ctx),
      ...buildTakeOtherYearChips(ctx),
      {
        label: "Show me my scope breakdown",
        clientAction: { type: "showScopeBreakdown" }
      },
      {
        label: "Show me my biggest sources",
        clientAction: { type: "showSourceBreakdown" }
      },
      {
        label: "What's incomplete?",
        clientAction: { type: "showCompleteness" }
      },
      {
        label: "Show me reduction ideas",
        prompt: "What are my best opportunities to reduce my biggest source?"
      },
      {
        label: "Download my report",
        clientAction: { type: "downloadPdfReport" }
      }
    ],
    cards: [buildHeadlineMetricCard(ctx)]
  }),
  "empty-fallback": () => FALLBACK_OPENER
};

const PRODUCTS_TAB_STATE_RULES = [
  { state: "empty-fallback", match: (ctx) => !ctx },
  {
    state: "pending-bom-requests",
    match: (ctx) =>
      (ctx.pendingBomRequests?.length || 0) > 0 &&
      (ctx.productsSummary?.count || 0) === 0
  },
  {
    state: "mixed-pending-and-catalog",
    match: (ctx) =>
      (ctx.pendingBomRequests?.length || 0) > 0 &&
      (ctx.productsSummary?.count || 0) > 0
  },
  {
    state: "products-no-requests",
    match: (ctx) => (ctx.productsSummary?.count || 0) > 0
  },
  {
    state: "received-products-only",
    match: (ctx) =>
      (ctx.receivedProductsCount || 0) > 0 &&
      (ctx.productsSummary?.count || 0) === 0 &&
      (ctx.pendingBomRequests?.length || 0) === 0
  },
  { state: "no-products-no-requests", match: () => true }
];

export const resolveProductsTabState = (ctx) =>
  (
    PRODUCTS_TAB_STATE_RULES.find(({ match }) => match(ctx || {})) || {
      state: "empty-fallback"
    }
  ).state;

const buildStartBomSurveyCard = ({
  lifecycleEventId,
  productId,
  productName,
  buyerName
}) => ({
  type: "action_card",
  props: {
    label: `Start BOM walkthrough for ${productName || "this product"}`,
    actionType: "start_bom_survey",
    description: buyerName
      ? `Scout walks you through confirming this product's materials, weight, packaging, and shipping route — then shares it with ${buyerName}.`
      : "Scout walks you through confirming this product's materials, weight, packaging, and shipping route — then shares it with your customer.",
    payload: {
      lifecycleEventId,
      productId,
      productName,
      buyerName
    }
  }
});

const PRODUCTS_OPENERS = {
  "no-products-no-requests": () => ({
    greeting:
      "Welcome to your Products tab. This is where you'll respond to BOM confirmation requests from customers who buy from you. Nothing pending right now — I'll let you know when a request comes in.",
    suggestedChips: [
      {
        label: "What's a BOM?",
        prompt: "What is a Bill of Materials (BOM)?"
      }
    ],
    cards: []
  }),
  "products-no-requests": (ctx) => {
    const count = ctx.productsSummary?.count || 0;
    const noun = count === 1 ? "product" : "products";
    return {
      greeting: `Your catalog has ${count} ${noun}. No customers waiting on data right now.`,
      suggestedChips: [
        {
          label: "Show me my products",
          prompt: "List my products."
        }
      ],
      cards: []
    };
  },
  "pending-bom-requests": (ctx) => {
    const requests = ctx.pendingBomRequests || [];
    const top = requests[0] || {};
    return {
      greeting: top.buyerName
        ? `${top.buyerName} is asking you to confirm the BOM for "${top.productName || "your product"}." Let's walk through it together.`
        : "You have a pending BOM request — let's walk through it.",
      suggestedChips: top.lifecycleEventId
        ? [
            {
              label: "Start the BOM walkthrough",
              clientAction: {
                type: "start_bom_survey",
                payload: { lifecycleEventId: top.lifecycleEventId }
              }
            }
          ]
        : [],
      cards: requests.slice(0, 3).map((req) =>
        buildStartBomSurveyCard({
          lifecycleEventId: req.lifecycleEventId,
          productId: req.productId,
          productName: req.productName,
          buyerName: req.buyerName
        })
      )
    };
  },
  "mixed-pending-and-catalog": (ctx) => {
    const pendingCount = (ctx.pendingBomRequests || []).length;
    const noun = pendingCount === 1 ? "customer is" : "customers are";
    const topRequest = (ctx.pendingBomRequests || [])[0];
    return {
      greeting: `${pendingCount} ${noun} waiting on BOM data — want to handle ${pendingCount === 1 ? "that" : "those"} first?`,
      suggestedChips: [
        {
          label: "Show me what's pending",
          prompt: "Show me my pending BOM requests."
        },
        {
          label: "Show me my products",
          prompt: "List my products."
        }
      ],
      cards: topRequest
        ? [
            buildStartBomSurveyCard({
              lifecycleEventId: topRequest.lifecycleEventId,
              productId: topRequest.productId,
              productName: topRequest.productName,
              buyerName: topRequest.buyerName
            })
          ]
        : []
    };
  },
  "received-products-only": (ctx) => {
    const count = ctx.receivedProductsCount || 0;
    const noun = count === 1 ? "product" : "products";
    return {
      greeting: `You have ${count} ${noun} shared with you by suppliers. Manage those on the Suppliers tab.`,
      suggestedChips: [
        {
          label: "Open Suppliers",
          clientAction: {
            type: "navigate",
            payload: { href: "/explorer/dashboard?view=suppliers" }
          }
        }
      ],
      cards: [
        {
          type: "navigate_card",
          props: {
            label: "Open Suppliers tab",
            href: "/explorer/dashboard?view=suppliers",
            description: "View products shared by your suppliers."
          }
        }
      ]
    };
  },
  "empty-fallback": buildEmptyTabOpener("Products")
};

const CUSTOMERS_STATE_RULES = [
  {
    state: "customer-empty-fallback",
    match: (c) => !c || !c.customersSummary
  },
  {
    state: "customer-focused-on-buyer",
    match: (c) => Boolean(c.focusedRelationshipId)
  },
  {
    state: "customer-zero-buyers",
    match: (c) => (c.customersSummary?.totalCount || 0) === 0
  },
  {
    state: "customer-with-pending-requests-needs-survey",
    match: (c) =>
      (c.pendingRequests?.length || 0) > 0 &&
      c.pendingRequests.some((r) => !r.hasCompletedSurveyForYear)
  },
  {
    state: "customer-with-pending-incoming-requests",
    match: (c) => (c.pendingRequests?.length || 0) > 0
  },
  {
    state: "customer-with-pending-shares-awaiting-acceptance",
    match: (c) => (c.pendingShares?.length || 0) > 0
  },
  {
    state: "customer-all-accepted",
    match: (c) => (c.customersSummary?.acceptedCount || 0) > 0
  },
  {
    state: "customer-with-revoked-only",
    match: (c) => (c.customersSummary?.revokedCount || 0) > 0
  }
];

export const resolveCustomersTabState = (ctx) =>
  (
    CUSTOMERS_STATE_RULES.find(({ match }) => match(ctx || {})) || {
      state: "customer-empty-fallback"
    }
  ).state;

const buildShareCustomerYearCard = ({
  relationshipId,
  year,
  buyerName,
  hasCompletedSurveyForYear
}) => ({
  type: "action_card",
  props: {
    label: hasCompletedSurveyForYear
      ? `Share ${year} survey with ${buyerName || "customer"}`
      : `Complete your ${year} survey to share with ${buyerName || "customer"}`,
    actionType: hasCompletedSurveyForYear
      ? "share_customer_year"
      : "take_emissions_survey",
    description: hasCompletedSurveyForYear
      ? `${buyerName || "Your customer"} will see your ${year} aggregate footprint and scope breakdown — not individual transactions. You can revoke any time.`
      : `Walk through the ${year} emissions survey first. After it's complete you'll be able to share with ${buyerName || "your customer"}.`,
    payload: { relationshipId, year, buyerName }
  }
});

const formatBuyerList = (requests) => {
  const names = requests.map((r) => r.buyerName).filter(Boolean);
  if (names.length === 0) {
    return "Your customer";
  }
  if (names.length === 1) {
    return names[0];
  }
  if (names.length === 2) {
    return `${names[0]} and ${names[1]}`;
  }
  return `${names[0]} and ${names.length - 1} other customers`;
};

const CUSTOMERS_OPENERS = {
  "customer-empty-fallback": buildEmptyTabOpener("Customers"),
  "customer-zero-buyers": (ctx) => {
    const hasAnySurvey = (ctx?.availableYears || []).length > 0;
    return {
      greeting:
        "You haven't connected with any customers yet. When a customer invites you to share your emissions, it'll show up here.",
      suggestedChips: [
        ...(hasAnySurvey
          ? []
          : [
              {
                label: "Take the emissions survey",
                clientAction: { type: "take_emissions_survey" }
              }
            ]),
        {
          label: "How does customer sharing work?",
          prompt: "How does sharing emissions data with customers work?"
        }
      ],
      cards: []
    };
  },
  "customer-focused-on-buyer": (ctx) => {
    const buyerName = ctx.focusedBuyerName || "this customer";
    const relevantRequest = (ctx.pendingRequests || []).find(
      (r) => r.relationshipId === ctx.focusedRelationshipId
    );
    return {
      greeting: `Here's your relationship with ${buyerName}.`,
      suggestedChips: relevantRequest
        ? [
            relevantRequest.hasCompletedSurveyForYear
              ? {
                  label: `Share my ${relevantRequest.year} survey with ${buyerName}`,
                  clientAction: {
                    type: "share_customer_year",
                    payload: {
                      relationshipId: relevantRequest.relationshipId,
                      year: relevantRequest.year
                    }
                  }
                }
              : {
                  label: `Complete your ${relevantRequest.year} survey to share with ${buyerName}`,
                  clientAction: {
                    type: "take_emissions_survey",
                    payload: { year: relevantRequest.year }
                  }
                }
          ]
        : [],
      cards: relevantRequest
        ? [buildShareCustomerYearCard(relevantRequest)]
        : []
    };
  },
  "customer-with-pending-requests-needs-survey": (ctx) => {
    const requests = ctx.pendingRequests || [];
    const firstNeedy =
      requests.find((r) => !r.hasCompletedSurveyForYear) || requests[0];
    const buyerName = firstNeedy?.buyerName || "Your customer";
    const year = firstNeedy?.year || mostRecentRecommendableYear();
    return {
      greeting: `${buyerName} needs your ${year} emissions data, but your ${year} survey isn't done yet. Want me to walk you through it?`,
      suggestedChips: [
        {
          label: `Start my ${year} survey`,
          clientAction: {
            type: "take_emissions_survey",
            payload: { year }
          }
        },
        {
          label: "Show me what's pending",
          prompt: "Show me my pending customer requests."
        }
      ],
      cards: requests.slice(0, 3).map(buildShareCustomerYearCard)
    };
  },
  "customer-with-pending-incoming-requests": (ctx) => {
    const requests = ctx.pendingRequests || [];
    const first = requests[0] || {};
    const canShareFirst =
      first.relationshipId && first.year && first.hasCompletedSurveyForYear;
    const firstActionChip = canShareFirst
      ? {
          label: `Share my ${first.year} survey with ${first.buyerName || "customer"}`,
          clientAction: {
            type: "share_customer_year",
            payload: {
              relationshipId: first.relationshipId,
              year: first.year
            }
          }
        }
      : null;
    return {
      greeting: `${formatBuyerList(requests)} ${requests.length === 1 ? "has" : "have"} asked you to share emissions data. Want to handle ${first.buyerName || "the first"}'s ${first.year || ""} request first? It takes one click if your ${first.year || ""} survey is complete.`,
      suggestedChips: [
        ...(firstActionChip ? [firstActionChip] : []),
        {
          label: "Show me everyone who's asked",
          prompt: "Show me all my pending customer requests."
        }
      ],
      cards: requests.slice(0, 3).map(buildShareCustomerYearCard)
    };
  },
  "customer-with-pending-shares-awaiting-acceptance": (ctx) => {
    const shares = ctx.pendingShares || [];
    const first = shares[0] || {};
    return {
      greeting: `You've shared data with ${first.buyerName || "your customer"} — they're reviewing it. Want me to show you who's seen what?`,
      suggestedChips: [
        {
          label: "Show me my share status",
          prompt: "Show me which customers have accepted my shared data."
        }
      ],
      cards: shares.length
        ? [
            {
              type: "list_card",
              props: {
                title: "Shares waiting on review",
                items: shares.slice(0, 5).map((share) => ({
                  label: `${share.buyerName || "Customer"} — ${share.year}`,
                  value: "Awaiting acceptance"
                }))
              }
            }
          ]
        : []
    };
  },
  "customer-all-accepted": () => ({
    greeting:
      "Looks like everyone you've shared with has accepted. Anything else I can help with?",
    suggestedChips: [
      {
        label: "Show me my biggest customer",
        prompt: "Which customer has the highest downstream emissions?"
      },
      {
        label: "Who sees my data?",
        prompt: "Which customers can see my emissions data?"
      }
    ],
    cards: []
  }),
  "customer-with-revoked-only": () => ({
    greeting:
      "Looks like you've revoked all your shares. Want to revisit any of them?",
    suggestedChips: [
      {
        label: "Show me revoked shares",
        prompt: "List the customers I've revoked sharing with."
      }
    ],
    cards: []
  })
};

const SUPPLIERS_STATE_RULES = [
  { state: "empty-fallback", match: (ctx) => !ctx || !ctx.suppliersSummary },
  {
    state: "no-suppliers",
    match: (ctx) => (ctx.suppliersSummary?.totalRelationshipCount || 0) === 0
  },
  {
    state: "suppliers-mixed",
    match: (ctx) =>
      (ctx.suppliersSummary?.pendingSharesCount || 0) > 0 &&
      (ctx.suppliersSummary?.confirmedSharesCount || 0) > 0
  },
  {
    state: "suppliers-with-pending-shares",
    match: (ctx) => (ctx.suppliersSummary?.pendingSharesCount || 0) > 0
  },
  {
    state: "suppliers-with-confirmed-shares",
    match: (ctx) => (ctx.suppliersSummary?.confirmedSharesCount || 0) > 0
  },
  { state: "suppliers-no-shares", match: () => true }
];

export const resolveSuppliersState = (ctx) =>
  (
    SUPPLIERS_STATE_RULES.find(({ match }) => match(ctx || {})) || {
      state: "empty-fallback"
    }
  ).state;

const buildOpenSupplierShareReviewCard = ({
  relationshipId,
  vendorName,
  year
}) => ({
  type: "action_card",
  props: {
    label: `Accept ${vendorName || "this supplier"}'s ${year} share`,
    actionType: "open_supplier_share_review",
    description: `Add ${vendorName || "this supplier"}'s ${year} emissions data into your scope 3 footprint. They keep the ability to revoke; so do you.`,
    payload: { relationshipId, year, vendorName }
  }
});

const SUPPLIERS_OPENERS = {
  "empty-fallback": buildEmptyTabOpener("Suppliers"),
  "no-suppliers": () => ({
    greeting:
      "This is where supplier data will appear when your vendors share it back. Your transactions haven't been classified into supplier vendors yet — let's look at your own emissions instead. To actively invite and manage suppliers, upgrade to Aclymate Navigator.",
    suggestedChips: [
      {
        label: "Show me my biggest emissions",
        prompt: "What's my biggest emission source?"
      },
      {
        label: "What can Scout do here?",
        prompt:
          "What does the Suppliers tab show and how does supplier data sharing work?"
      }
    ],
    cards: [
      {
        type: "navigate_card",
        props: {
          label: "Open the Footprint tab",
          href: "/explorer/dashboard?view=footprint",
          description:
            "See your own emissions while you wait for supplier data."
        }
      },
      {
        type: "navigate_card",
        props: {
          label: "Upgrade to Navigator",
          href: "/explorer/upgrade",
          description:
            "Invite suppliers, request survey data, and track it over time."
        }
      }
    ]
  }),
  "suppliers-no-shares": (ctx) => {
    const count = ctx.suppliersSummary?.totalRelationshipCount || 0;
    return {
      greeting: `You have ${count} supplier${count === 1 ? "" : "s"} in your catalog — none have shared data back yet. Outbound supplier-survey requests aren't available in Explorer yet; want to see what your suppliers look like as a group?`,
      suggestedChips: [
        {
          label: "Show me my biggest vendors",
          prompt: "Show me my biggest vendors by spend."
        }
      ],
      cards: []
    };
  },
  "suppliers-with-pending-shares": (ctx) => {
    const pending = ctx.pendingSupplierShares || [];
    const count = pending.length;
    return {
      greeting: `${count} supplier${count === 1 ? " is" : "s are"} waiting on your review — accept each one to bring their shared data in.`,
      suggestedChips: [
        {
          label: "Show me what's pending",
          prompt: "Show me my pending supplier shares."
        },
        {
          label: "Summarize this year's supplier emissions",
          prompt: "Summarize supplier emissions for this year."
        }
      ],
      cards: pending.slice(0, 3).map(buildOpenSupplierShareReviewCard)
    };
  },
  "suppliers-with-confirmed-shares": (ctx) => {
    const count = ctx.suppliersSummary?.confirmedSharesCount || 0;
    const items = ctx.suppliersSummary?.confirmedItems || [];
    return {
      greeting: `${count} supplier${count === 1 ? " has" : "s have"} shared data with you. Here's the lineup.`,
      suggestedChips: [
        {
          label: "Which supplier is my biggest source?",
          prompt: "Which supplier is my biggest emission source?"
        },
        {
          label: "Show me products with shared BOM data",
          prompt: "Show me products with shared BOM data."
        }
      ],
      cards: items.length
        ? [
            {
              type: "list_card",
              props: {
                title: "Suppliers who've shared data",
                items: items.map((item) => ({
                  label: item.vendorName,
                  value: item.latestYear
                    ? `Latest: ${item.latestYear}`
                    : item.hasReport
                      ? "Annual report"
                      : item.hasBom
                        ? "Product BOM"
                        : "Shared"
                }))
              }
            }
          ]
        : []
    };
  },
  "suppliers-mixed": (ctx) => {
    const pending = ctx.pendingSupplierShares || [];
    const summary = ctx.suppliersSummary || {};
    return {
      greeting: `${summary.pendingSharesCount || 0} pending and ${summary.confirmedSharesCount || 0} confirmed — let's clear the pending ones first.`,
      suggestedChips: [
        {
          label: "Show me what's pending",
          prompt: "Show me my pending supplier shares."
        }
      ],
      cards: pending.slice(0, 3).map(buildOpenSupplierShareReviewCard)
    };
  }
};

export const DASHBOARD_OPENERS = {
  dashboard: FOOTPRINT_OPENERS,
  footprint: FOOTPRINT_OPENERS,
  products: {
    byState: PRODUCTS_OPENERS,
    resolveState: resolveProductsTabState
  },
  customers: {
    byState: CUSTOMERS_OPENERS,
    resolveState: resolveCustomersTabState
  },
  suppliers: {
    byState: SUPPLIERS_OPENERS,
    resolveState: resolveSuppliersState
  }
};

const isTabMapWithResolver = (tabMap) =>
  tabMap &&
  typeof tabMap === "object" &&
  typeof tabMap.resolveState === "function" &&
  tabMap.byState &&
  typeof tabMap.byState === "object";

const dashboardOpener = (ctx) => {
  const safeCtx = ctx || {};
  const activeTab = safeCtx.activeTab || "dashboard";
  const tabMap = DASHBOARD_OPENERS[activeTab] || DASHBOARD_OPENERS.dashboard;
  const useTabResolver = isTabMapWithResolver(tabMap);
  const state = useTabResolver
    ? tabMap.resolveState(safeCtx)
    : resolveSurfaceState(safeCtx);
  const stateMap = useTabResolver ? tabMap.byState : tabMap;
  const builder =
    stateMap[state] ||
    stateMap["empty-fallback"] ||
    DASHBOARD_OPENERS.footprint["empty-fallback"];
  return builder(safeCtx);
};

export const surfaceOpenerMap = {
  dashboard: dashboardOpener
};

const stripShareActions = (opener) => ({
  ...opener,
  suggestedChips: (opener.suggestedChips || []).filter(
    (chip) => !SHARE_ACTION_TYPES.has(chip?.clientAction?.type)
  ),
  cards: (opener.cards || []).filter(
    (card) => !SHARE_ACTION_TYPES.has(card?.props?.actionType)
  )
});

export const getSurfaceOpener = (surface, surfaceContext) => {
  const entry = surfaceOpenerMap[surface];
  if (!entry) {
    console.error(
      `[scoutOpeners] Unknown surface "${surface}" — falling back to generic opener. Known surfaces: ${Object.keys(surfaceOpenerMap).join(", ")}`
    );
    return FALLBACK_OPENER;
  }
  const opener =
    typeof entry === "function" ? entry(surfaceContext || {}) : entry;
  if ((surfaceContext || {}).canShare === false) {
    return stripShareActions(opener);
  }
  return opener;
};
